import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../environments/environment';
import { BreadcrumbItem, createBreadcrumbList } from '../data/structured-data';

export interface SeoData {
  title: string;
  description: string;
  keywords?: string;
  jsonLdBlocks?: object[];
  breadcrumbItems?: BreadcrumbItem[];
}

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT) as Document;

  update(seoData: SeoData): void {
    this.updateBase(seoData);
    this.updateOpenGraph(seoData);
    this.updateTwitterCard(seoData);
    this.updateCanonicalUrl();
    this.updateJsonLd(seoData);
  }

  private updateBase(seoData: SeoData): void {
    this.title.setTitle(seoData.title);
    this.meta.updateTag({ name: 'description', content: seoData.description });
    this.meta.updateTag({ name: 'keywords', content: seoData.keywords ?? '' });
  }

  private updateOpenGraph(seoData: SeoData): void {
    const ogData = {
      'og:title': seoData.title,
      'og:description': seoData.description,
      'og:type': 'website',
      'og:site_name': 'Mari Group',
      'og:url': environment.webUrl + this.router.url,
      'og:image': environment.webUrl + '/og-img.jpg',
      'og:image:alt': seoData.title,
    };

    Object.entries(ogData).forEach(([property, content]) => {
      if (content) {
        this.meta.updateTag({ property, content });
      }
    });
  }

  private updateTwitterCard(seoData: SeoData): void {
    const twitterData = {
      'twitter:card': 'summary_large_image',
      'twitter:title': seoData.title,
      'twitter:description': seoData.description,
      'twitter:image': environment.webUrl + '/images/og-default.png',
      'twitter:image:alt': seoData.title,
    };

    Object.entries(twitterData).forEach(([name, content]) => {
      if (content) {
        this.meta.updateTag({ name, content });
      }
    });
  }

  private updateCanonicalUrl(): void {
    if (!this.document?.head) {
      return;
    }

    const currentUrl = this.router.url;
    const cleanPath = currentUrl.split('?')[0]?.split('#')[0] ?? currentUrl;
    const url = environment.webUrl + cleanPath;

    // Remove existing canonical link
    const existingCanonical = this.document.querySelector(
      'link[rel="canonical"]'
    );
    if (existingCanonical) {
      existingCanonical.remove();
    }

    // Add new canonical link
    const link = this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    this.document.head.appendChild(link);
  }

  private updateJsonLd(seoData: SeoData): void {
    if (!this.document?.head) {
      return;
    }

    this.document
      .querySelectorAll('script[data-seo-jsonld="true"]')
      .forEach((script) => script.remove());

    const blocks = [...(seoData.jsonLdBlocks ?? [])];

    if (this.router.url !== '/') {
      const breadcrumbItems =
        seoData.breadcrumbItems ?? this.buildDefaultBreadcrumb(seoData.title);
      blocks.push(createBreadcrumbList(breadcrumbItems));
    }

    blocks.forEach((block) => {
      const script = this.document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-jsonld', 'true');
      script.text = JSON.stringify(block);
      this.document.head.appendChild(script);
    });
  }

  private buildDefaultBreadcrumb(pageTitle: string): BreadcrumbItem[] {
    const path = this.router.url.split('?')[0]?.split('#')[0] ?? this.router.url;

    return [
      { name: 'Home', path: '/' },
      {
        name: this.cleanTitleForBreadcrumb(pageTitle),
        path,
      },
    ];
  }

  private cleanTitleForBreadcrumb(value: string): string {
    return value
      .replace(/\s*[|–-]\s*MARI Group$/i, '')
      .replace(/\s*[|–-]\s*MARI$/i, '')
      .trim();
  }
}
